// Application Insights configuration for KlaraView frontend usage telemetry.
//
// Captures identity-attributed usage of the *logged-in* app: page views
// (state-driven via AppView), custom events, and automatic geo/device data.
// Marketing-site visitor analytics is handled separately by Microsoft Clarity.
//
// Initialization is defensive: if no connection string is configured (e.g.
// local dev without the env var) every helper becomes a no-op so the app
// keeps working without telemetry.

import { ApplicationInsights } from '@microsoft/applicationinsights-web';

const CONNECTION_STRING = process.env.REACT_APP_APPINSIGHTS_CONNECTION_STRING || '';

let appInsights: ApplicationInsights | null = null;

/**
 * Initialize Application Insights once. Safe to call multiple times.
 * No-op when no connection string is configured.
 */
export const initAppInsights = (): void => {
  if (appInsights || !CONNECTION_STRING || typeof window === 'undefined') {
    return;
  }

  appInsights = new ApplicationInsights({
    config: {
      connectionString: CONNECTION_STRING,
      // No react-router — navigation is state-driven, so we send page views manually.
      enableAutoRouteTracking: false,
      // Correlate frontend calls with the Functions backend.
      enableCorsCorrelation: true,
      enableRequestHeaderTracking: true,
      enableResponseHeaderTracking: true,
      // Capture unhandled exceptions.
      autoTrackPageVisitTime: true,
      disableFetchTracking: false,
    },
  });

  appInsights.loadAppInsights();
};

/**
 * Stamp every subsequent event with the signed-in user + customer.
 * Makes demo accounts (e.g. vasakronan) appear as distinct, named users.
 */
export const setTelemetryUser = (email: string, customerId: string): void => {
  if (!appInsights || !email) {
    return;
  }
  // accountId = customerId so telemetry can be sliced per tenant.
  appInsights.setAuthenticatedUserContext(
    sanitizeId(email),
    customerId ? sanitizeId(customerId) : undefined,
    true
  );
};

/** Clear identity on logout. */
export const clearTelemetryUser = (): void => {
  if (!appInsights) {
    return;
  }
  appInsights.clearAuthenticatedUserContext();
};

/** Track a state-driven page/view change. */
export const trackPageView = (name: string, properties?: Record<string, unknown>): void => {
  if (!appInsights) {
    return;
  }
  appInsights.trackPageView({ name, properties });
};

/** Track a discrete usage event (e.g. report_generated, image_viewed). */
export const trackEvent = (name: string, properties?: Record<string, unknown>): void => {
  if (!appInsights) {
    return;
  }
  appInsights.trackEvent({ name }, properties as { [key: string]: unknown });
};

// App Insights authenticated-user/account IDs must not contain certain
// characters (comma, semicolon, equals, space). Strip them defensively.
const sanitizeId = (value: string): string => value.replace(/[,;=\s]/g, '_');
